import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes, ElementType } from 'react';
import { cn } from '@/lib/utils/cn';

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full text-left text-sm', className)}>{children}</table>
    </div>
  );
}

export function THead({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <thead
      className={cn(
        'border-b border-border bg-surface-muted/50',
        'text-[10.5px] font-semibold uppercase tracking-widest text-fg-subtle',
        className,
      )}
    >
      {children}
    </thead>
  );
}

export function TBody({ children, className }: { children: ReactNode; className?: string }) {
  return <tbody className={cn('divide-y divide-border/60', className)}>{children}</tbody>;
}

export function TR({ children, className, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn('transition-colors duration-100 hover:bg-surface-subtle/50', className)}
      {...rest}
    >
      {children}
    </tr>
  );
}

export function TH({ children, className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn('whitespace-nowrap px-4 py-2.5', className)}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TD({ children, className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn('whitespace-nowrap px-4 py-3 text-fg', className)} {...rest}>
      {children}
    </td>
  );
}

export function EmptyState({
  message,
  icon: Icon,
}: {
  message: string;
  icon?: ElementType;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
      {Icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-subtle ring-1 ring-border">
          <Icon className="h-5 w-5 text-fg-subtle" aria-hidden />
        </div>
      )}
      <p className="max-w-xs text-sm text-fg-muted">{message}</p>
    </div>
  );
}
